// The Registrar the browser's caches and mounted pages were answered for.
//
// Owner decision CF-27 (2026-10-13), docs/shape-ops-tenant-selector.md §9: the
// active Registrar lives in the server session and changes only by the explicit
// selection in the menu, and every tab of the session works on it. The browser
// holds no choice of its own and names none on its requests; it follows what the
// session reports. What this module holds is which Registrar the caches and the
// pages belong to, so that a change of it, made in this tab or in another one,
// drops both before anything is drawn for the new one.
//
// A module store rather than React state: the shell keys the workspace by its
// switch counter, and the selection's answer must move the counter and empty
// the caches in the same step that hands the new session to React.
import type { QueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@simsm/shared-types';

const CHANNEL_NAME = 'simsm.active-registrar';

interface SessionAnnouncement {
  kind: 'session';
  session: SessionUser;
}

let active = '';
let epoch = 0;
const listeners = new Set<() => void>();
let channel: BroadcastChannel | null | undefined;

export function getActiveRegistrar(): string {
  return active;
}

/**
 * Counts the times a Registrar that was held has been replaced. The shell keys
 * the workspace by it, so page-local state (a picked location, an open dialog, a
 * filter) cannot carry the Registrar being left. The first Registrar after
 * sign-in leaves it alone: there was nothing held to leave.
 */
export function getRegistrarEpoch(): number {
  return epoch;
}

export function subscribeActiveRegistrar(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** Records the Registrar the pages are now for; the counter moves when one held is replaced. */
export function setActiveRegistrar(idno: string): void {
  if (idno === active) return;
  if (active) epoch += 1;
  active = idno;
  emit();
}

/**
 * Forgets it without counting a switch. Sign-out, a role switch and the two
 * landings of an authentication call it: what follows is a new start, and the
 * page it lands on is mounted anew anyway.
 */
export function clearActiveRegistrar(): void {
  active = '';
  emit();
}

/**
 * The Registrar `session` acts for: the one the bff reports, and for a scope of
 * one Registrar that one, the central policy's own rule (CF-27).
 */
export function activeRegistrarOf(session: SessionUser): string {
  const scope = session.registrarScope ?? [];
  return session.activeRegistrar ?? (scope.length === 1 ? (scope[0] ?? '') : '');
}

/**
 * Follows a session read. When it acts for another Registrar than the one the
 * caches hold, every query but the session's is reset and the counter moves,
 * so nothing answered for the Registrar being left is drawn under the new one.
 */
export function followSession(queryClient: QueryClient, session: SessionUser | null): void {
  const next = session ? activeRegistrarOf(session) : '';
  if (next === active) return;
  if (active) {
    void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
  }
  setActiveRegistrar(next);
}

/** The session a selection answered, from this tab or another: followed, then cached. */
export function applySession(queryClient: QueryClient, session: SessionUser): void {
  followSession(queryClient, session);
  queryClient.setQueryData(['session'], session);
}

/**
 * A role switch: the session now acts as another role, and every answer cached
 * for the one that was left (stock rows, lists, counts) is for the wrong
 * audience. All queries but the session's are reset, the new session is written
 * and the Registrar held is forgotten (it belonged to the role left), so nothing
 * read under the previous role is drawn under the new one (BUG-1-001).
 */
export function applyRoleSwitch(queryClient: QueryClient, session: SessionUser): void {
  void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
  queryClient.setQueryData(['session'], session);
  clearActiveRegistrar();
}

/**
 * Tells the other tabs of this browser what a selection answered. They share
 * the session, so the server already acts for the new Registrar on their
 * requests; this makes them name it and drop what they read for the old one.
 */
export function announceSession(session: SessionUser): void {
  const announcement: SessionAnnouncement = { kind: 'session', session };
  sharedChannel()?.postMessage(announcement);
}

/** Applies what another tab's selection answered, until the returned function is called. */
export function followOtherTabs(queryClient: QueryClient): () => void {
  const listening = sharedChannel();
  if (!listening) return () => undefined;
  const onMessage = (event: MessageEvent<unknown>): void => {
    const data = event.data as Partial<SessionAnnouncement> | null;
    if (data?.kind === 'session' && data.session) applySession(queryClient, data.session);
  };
  listening.addEventListener('message', onMessage);
  return () => listening.removeEventListener('message', onMessage);
}

// One channel per tab, for posting and listening alike: a channel never hears
// its own messages, so the tab that selected does not apply its answer twice.
function sharedChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : null;
  }
  return channel;
}

function emit(): void {
  listeners.forEach((listener) => listener());
}
